import React, { useCallback, useMemo } from "react";
import {
  StaffActionRow,
  YesNo,
} from "../../../../../../src/api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../../../src/app/store";
import {
  addStaffSideActionRow,
  deleteStaffSideActionRow,
  selectFMR1DraftStaffSideActionRows,
  updateStaffSideActionRow,
} from "../../../logic/frm1Slice";
import { getInsertedSortOrder, sortRowsBySortOrder, YES_NO_OPTIONS } from "../common/utils";
import { ColumnType } from "../../../../../components/ui/baseDataTable";
import { FMRTable, FMRTableColumn } from "../common/FMRTable";
import { generateRowId } from "../common/utils";



const buildEmptyRow = (): StaffActionRow => ({
  row_id: generateRowId(),
  isDirty: true,
  isAdded: true,
});

export const StaffSideActionTable: React.FC = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(selectFMR1DraftStaffSideActionRows);
  const sortedRows = useMemo(() => sortRowsBySortOrder(rows), [rows]);

  const handleAddRow = useCallback((rowIndex: number) => {
    const prev = sortedRows.length > 0 ? sortedRows[rowIndex]: undefined;
    const next = rowIndex < (sortedRows.length - 1)? sortedRows[rowIndex + 1] : undefined;

    const elementToAdd: StaffActionRow = { 
      ...buildEmptyRow(), 
      sort_order: getInsertedSortOrder(prev?.sort_order, next?.sort_order), 
      sno: (prev?.sno || 0) + 1
    };

    const rows_below = sortedRows.slice(rowIndex+1);
    rows_below.forEach((row) => {
      dispatch(updateStaffSideActionRow({id: row.row_id, changes: {sno: (row.sno ?? 0) + 1}}))
    })
    dispatch(addStaffSideActionRow({ row: elementToAdd }));

  }, [dispatch, sortedRows]);

  const handleDeleteRow = useCallback(async (rowIndex: number) => {
    const deletedItem = sortedRows[rowIndex];
    if (!deletedItem) return;
    const rows_below = sortedRows.slice(rowIndex+1);
    rows_below.forEach((row) => {
      dispatch(updateStaffSideActionRow({id: row.row_id, changes: {sno: (row.sno ?? 0) - 1}}))
    })

    dispatch(deleteStaffSideActionRow({ id: deletedItem.row_id }));

    if (deletedItem.isAdded) return;
  }, [dispatch, sortedRows]);

  const handleChange = useCallback(
    (rowIndex: number, value: any, field: keyof StaffActionRow) => {
      const row = sortedRows[rowIndex];
      dispatch(updateStaffSideActionRow({ id: row.row_id, changes: { [field]: value } }));
    },
    [dispatch, sortedRows],
  );

  const columns: FMRTableColumn<StaffActionRow>[] = [
    {
      headerName: "Sr. No.",
      field: "sno",
      type: "text" as ColumnType,
      className: "w-[50px]",
      isEditable: false,
      getValue: (row: StaffActionRow) => row.sno,
    },

    {
      headerName: "Name",
      field: "name",
      className: "w-[110px]",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row: StaffActionRow) => row.name ?? "",
      onChange: (rowIndex: number, value: string) =>
        handleChange(rowIndex, value, "name"),
    },

    {
      headerName: "PAN of staff member",
      field: "pan",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row: StaffActionRow) => row.pan ?? "",
      onChange: (rowIndex: number, value: string) =>
        handleChange(rowIndex, value, "pan"),
    },

    {
      headerName: "C-KYC number",
      field: "c_kyc_number",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row: StaffActionRow) => row.c_kyc_number ?? "",
      onChange: (rowIndex: number, value: string) =>
        handleChange(rowIndex, value, "c_kyc_number"),
    },

    {
      headerName: "Designation",
      field: "designation",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row: StaffActionRow) =>
        row.designation ?? "",
      onChange: (rowIndex: number, value: string) =>
        handleChange(rowIndex, value, "designation"),
    },

    {
      headerName: "Type of examination",
      field: "type_of_examination",
      type: "select" as ColumnType,
      isEditable: true,
      options: [
        { name: "Vigilance", value: "Vigilance" },
        { name: "Non-Vigilance", value: "Non-Vigilance" },
      ],
      getValue: (row: StaffActionRow) =>
        row.type_of_examination ?? null,
      onChange: (
        rowIndex: number,
        value: "Vigilance" | "Non-Vigilance"
      ) =>
        handleChange(
          rowIndex,
          value,
          "type_of_examination"
        ),
    },

    {
      headerName: "Suspended",
      field: "suspended",
      type: "select" as ColumnType,
      isEditable: true,
      options: YES_NO_OPTIONS,
      getValue: (row: StaffActionRow) =>
        row.suspended ?? null,
      onChange: (rowIndex: number, value: boolean) =>
        handleChange(rowIndex, value, "suspended"),
    },

    {
      headerName: "Date of suspension",
      field: "date_of_suspension",
      type: "date" as ColumnType,
      isEditable: true,
      getValue: (row: StaffActionRow) =>
        row.date_of_suspension ?? null,
      onChange: (rowIndex: number, value: string) =>
        handleChange(
          rowIndex,
          value,
          "date_of_suspension"
        ),
    },

    {
      headerName: "Date of issue of charge sheet",
      field: "date_of_issue_of_charge_sheet",
      type: "date" as ColumnType,
      isEditable: true,
      getValue: (row: StaffActionRow) =>
        row.date_of_issue_of_charge_sheet ?? null,
      onChange: (rowIndex: number, value: string) =>
        handleChange(
          rowIndex,
          value,
          "date_of_issue_of_charge_sheet"
        ),
    },

    {
      headerName:
        "Date of commencement of domestic enquiry",
      field: "date_of_commencement_of_domestic_enquiry",
      type: "date" as ColumnType,
      isEditable: true,
      getValue: (row: StaffActionRow) =>
        row.date_of_commencement_of_domestic_enquiry ??
        null,
      onChange: (rowIndex: number, value: string) =>
        handleChange(
          rowIndex,
          value,
          "date_of_commencement_of_domestic_enquiry"
        ),
    },

    {
      headerName: "Date of completion of enquiry",
      field: "date_of_completion_of_enquiry",
      type: "date" as ColumnType,
      isEditable: true,
      getValue: (row: StaffActionRow) =>
        row.date_of_completion_of_enquiry ??
        null,
      onChange: (rowIndex: number, value: string) =>
        handleChange(
          rowIndex,
          value,
          "date_of_completion_of_enquiry"
        ),
    },
    {
      headerName: "Date of issue of final orders",
      field: "date_of_issue_of_final_orders",
      type: "date" as ColumnType,
      isEditable: true,
      getValue: (row: StaffActionRow) =>
        row.date_of_issue_of_final_orders ?? null,
      onChange: (rowIndex: number, value: string) =>
        handleChange(
          rowIndex,
          value,
          "date_of_issue_of_final_orders"
        ),
    },

    {
      headerName: "Punishment Awarded",
      field: "punishment_awarded",
      type: "select" as ColumnType,
      isEditable: true,
      options: YES_NO_OPTIONS,
      getValue: (row: StaffActionRow) =>
        row.punishment_awarded ?? null,
      onChange: (rowIndex: number, value: boolean) =>
        handleChange(
          rowIndex,
          value,
          "punishment_awarded"
        ),
    },

    {
      headerName:
        "Details of prosecution / conviction / acquittal",
      field: "details_of_prosecution",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row: StaffActionRow) =>
        row.details_of_prosecution ?? "",
      onChange: (rowIndex: number, value: string) =>
        handleChange(
          rowIndex,
          value,
          "details_of_prosecution"
        ),
    },
  ]

  return (
    <div className="">
      <FMRTable
        columns={columns}
        rows={sortedRows}
        onAdd={handleAddRow}
        onDelete={handleDeleteRow}
        title={"Staff-side action"}
      />
    </div>
  );
};

StaffSideActionTable.displayName = "StaffSideActionTable";



